package com.writerslab.office

import kotlinx.serialization.Serializable

@Serializable
data class OfficeStats(
    val totalUsers: Long,
    val totalBooks: Long,
    val totalUserBooks: Long,
    val runningJobs: Long,
    val doneJobs: Long,
    val failedJobs: Long,
    val pipelinePaidDone: Long = 0,
    val pipelineFreeDone: Long = 0,
    val totalCreditsInCirculation: Long,
    val totalCodes: Long,
    val availableCodes: Long,
    val redeemedCodes: Long,
    val totalFreeAnnotations: Long = 0,
    val totalPaidAnnotations: Long = 0,
    val totalFreeCovers: Long = 0,
    val totalPaidCoverBatches: Long = 0,
    val totalPaidCoverRegens: Long = 0,
    val totalFreeTeasers: Long = 0,
    val totalPaidTeasers: Long = 0,
)

@Serializable
data class OfficeCode(
    val code: String,
    val credits: Int,
    val batchNote: String? = null,
    val createdBy: String? = null,
    val createdAt: String,
    val redeemedBy: String? = null,
    val redeemedAt: String? = null,
)

@Serializable
data class OfficeUser(
    val userId: String,
    val email: String,
    val balance: Int,
    val totalPaidCredits: Int,
    val totalCodeCredits: Int,
    val bookCount: Int,
    val pipelineJobsDone: Int,
    val pipelineCreditsCharged: Int,
    val freeAnnotationsUsed: Int,
    val paidAnnotationsUsed: Int = 0,
    val freeCoverBatchesUsed: Int,
    val paidCoverBatchesUsed: Int = 0,
    val paidCoverRegenUsed: Int = 0,
    val freeTeaserPreviewsUsed: Int,
    val paidTeaserPreviewsUsed: Int = 0,
    val createdAt: String,
)

@Serializable
data class OfficeAnalyticsBucket(
    val date: String,
    val registrations: Int,
    val payments: Int,
    val creditsViaPayment: Int,
    val codeRedemptions: Int,
    /** Distinct books analysed in this bucket. Not additive — see booksTotal. */
    val books: Int? = null,
    /** Distinct books over the whole period, repeated on every bucket. */
    val booksTotal: Int? = null,
    // Null on the week / month views: uniques are not additive across
    // days, so there is no honest weekly number to show.
    val visitors: Int? = null,
    val newVisitors: Int? = null,
    val loggedInVisitors: Int? = null,
)

/**
 * Per-service run counts for one bucket. Every service key is always
 * present with a zero — the backend zero-fills so a missing key can
 * never be mistaken for "no data".
 */
@Serializable
data class OfficeServiceBucket(
    val date: String,
    val runs: Map<String, Int>,
    val done: Map<String, Int>,
    val failed: Map<String, Int>,
)

@Serializable
data class GenerateCodesRequest(
    val count: Int,
    val credits: Int,
    val batchNote: String? = null,
)

@Serializable
data class GenerateCodesResponse(val codes: List<String>)

@Serializable
data class OfficeBook(
    val userId: String,
    val fingerprint: String,
    val filename: String,
    val wordCount: Int,
    val hasAiResult: Boolean,
    val analyzedAt: String,
    val hasStaticReport: Boolean,
    val hasPipelineReport: Boolean,
    val email: String? = null,
    val hasText: Boolean = false,
)

@Serializable
data class DownloadTicketRequest(val path: String)

@Serializable
data class DownloadTicketResponse(val ticket: String)

// ── Blog ────────────────────────────────────────────────────────────────
// Field names mirror the backend DTOs (OfficeBlogController) exactly.

@Serializable
data class BlogSummary(
    val id: Long,
    val slug: String,
    val title: String,
    val status: String,
    val language: String,
    val translationKey: String,
    val authorName: String,
    val readingTimeMinutes: Int,
    val likes: Int = 0,
    val dislikes: Int = 0,
    val views: Int = 0,
    val reads: Int = 0,
    val updatedAt: String,
    val publishedAt: String? = null,
)

@Serializable
data class TranslationRef(
    val id: Long,
    val language: String,
    val slug: String,
    val status: String,
    val title: String,
)

@Serializable
data class BlogArticle(
    val id: Long,
    val slug: String,
    val language: String,
    val translationKey: String,
    val status: String,
    val title: String,
    val excerpt: String? = null,
    val contentMarkdown: String,
    val coverImagePath: String? = null,
    val coverImageAlt: String? = null,
    val metaTitle: String? = null,
    val metaDescription: String? = null,
    val authorName: String,
    val readingTimeMinutes: Int,
    val likes: Int = 0,
    val dislikes: Int = 0,
    val views: Int = 0,
    val reads: Int = 0,
    val createdAt: String,
    val updatedAt: String,
    val publishedAt: String? = null,
    val translations: List<TranslationRef> = emptyList(),
)

@Serializable
data class BlogUpsertRequest(
    val title: String,
    val slug: String? = null,
    val language: String? = null,
    val translationKey: String? = null,
    val excerpt: String? = null,
    val contentMarkdown: String = "",
    val coverImagePath: String? = null,
    val coverImageAlt: String? = null,
    val metaTitle: String? = null,
    val metaDescription: String? = null,
    val authorName: String? = null,
)

@Serializable
data class BlogImageUploadResponse(val url: String, val path: String)

@Serializable
data class BlogPreviewRequest(val contentMarkdown: String, val language: String)
